import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, ChevronUp, X, Trash2, Check, Send, Mail, CheckCircle2, FileText, Building2, ArrowLeftRight, Package, Lock } from 'lucide-react';
import { useSelections, Selection } from './SelectionsContext';
import { useTheme } from '../styles/ThemeContext';
type FinalizeStep = 'review' | 'processing' | 'success';
interface SelectionsWidgetProps {
  onSwap?: (category: string, itemName: string) => void;
  isGalleryOpen?: boolean;
}
export function SelectionsWidget({
  onSwap,
  isGalleryOpen = false
}: SelectionsWidgetProps) {
  const {
    selections,
    totalPrice,
    recentlyAdded,
    clearRecentlyAdded,
    removeSelection,
    clearSelections,
    activePackage,
    clearActivePackage,
    isItemOverridingPackage,
    getPackageItemPrice
  } = useSelections();
  const {
    isDark
  } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [finalizeStep, setFinalizeStep] = useState<FinalizeStep>('review');
  // Show toast when new item is added
  useEffect(() => {
    if (recentlyAdded) {
      setShowToast(true);
      const timer = setTimeout(() => {
        setShowToast(false);
        clearRecentlyAdded();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [recentlyAdded, clearRecentlyAdded]);
  const handleFinalize = () => {
    setFinalizeStep('processing');
    // Simulate processing
    setTimeout(() => {
      setFinalizeStep('success');
    }, 2500);
  };
  const handleClose = () => {
    setIsExpanded(false);
    // Reset to review step after closing
    setTimeout(() => setFinalizeStep('review'), 300);
  };
  const handleStartOver = () => {
    clearSelections();
    setFinalizeStep('review');
    setIsExpanded(false);
  };
  const handleSwap = (category: string, itemName: string) => {
    setIsExpanded(false);
    onSwap?.(category, itemName);
  };
  const itemCount = selections.length;
  if (itemCount === 0 && !showToast && finalizeStep === 'review') {
    return null;
  }
  return <>
      {/* Toast Notification */}
      <AnimatePresence data-id="element-1614">
        {showToast && recentlyAdded && <motion.div initial={{
        opacity: 0,
        y: 20,
        x: 20
      }} animate={{
        opacity: 1,
        y: 0,
        x: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="fixed bottom-[140px] right-6 z-[100] pointer-events-none" data-id="element-1615">
            <div className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl backdrop-blur-xl border ${isDark ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400' : 'bg-emerald-500/20 border-emerald-400/30 text-emerald-700'}`} data-id="element-1616">
              <div className="p-1.5 rounded-full bg-emerald-500/30" data-id="element-1617">
                <Check className="w-4 h-4" data-id="element-1618" />
              </div>
              <div className="flex flex-col" data-id="element-1619">
                <span className="text-sm font-medium" data-id="element-1620">Added to selections</span>
                <span className={`text-xs ${isDark ? 'text-emerald-400/70' : 'text-emerald-600/70'}`} data-id="element-1621">
                  {recentlyAdded.itemName} - {recentlyAdded.optionName}
                </span>
              </div>
              <span className="text-sm font-bold ml-2" data-id="element-1622">
                +${recentlyAdded.price.toLocaleString()}
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Collapsed Widget */}
      <AnimatePresence data-id="element-1623">
        {!isExpanded && itemCount > 0 && <motion.button initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.8,
        opacity: 0
      }} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} onClick={() => setIsExpanded(true)} className={`fixed right-6 z-[90] flex items-center gap-3 px-4 py-3 rounded-2xl shadow-2xl backdrop-blur-xl border transition-all duration-300 ${isDark ? 'bg-slate-900/80 border-cyan-500/30 hover:border-cyan-500/50' : 'bg-white/80 border-teal-500/30 hover:border-teal-500/50'} ${isGalleryOpen ? 'bottom-[360px]' : 'bottom-[80px]'}`} data-id="element-1624">
            <div className={`relative p-2 rounded-xl ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-1625">
              <ShoppingBag className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1626" />
              <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${isDark ? 'bg-cyan-500 text-white' : 'bg-teal-600 text-white'}`} data-id="element-1627">
                {itemCount}
              </div>
            </div>
            <div className="flex flex-col items-start" data-id="element-1628">
              <span className={`text-xs font-medium ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-1629">
                Your Selections
              </span>
              <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1630">
                ${totalPrice.toLocaleString()}
              </span>
            </div>
            <ChevronUp className={`w-4 h-4 ml-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-1631" />
          </motion.button>}
      </AnimatePresence>

      {/* Expanded Panel */}
      <AnimatePresence data-id="element-1632">
        {isExpanded && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={finalizeStep === 'review' ? handleClose : undefined} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[95]" data-id="element-1633" />

            {/* Panel - Centered modal instead of bottom sheet */}
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className={`fixed top-8 bottom-8 right-6 w-full max-w-md z-[100] rounded-2xl shadow-2xl backdrop-blur-xl border flex flex-col overflow-hidden ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`} data-id="element-1634">
              {/* Review Step */}
              {finalizeStep === 'review' && <>
                  {/* Header */}
                  <div className={`flex items-center justify-between p-5 border-b flex-shrink-0 ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-1635">
                    <div className="flex items-center gap-3" data-id="element-1636">
                      <div className={`p-2 rounded-xl ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-1637">
                        <ShoppingBag className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1638" />
                      </div>
                      <div data-id="element-1639">
                        <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1640">
                          Your Selections
                        </h2>
                        <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-1641">
                          {itemCount} upgrade{itemCount !== 1 ? 's' : ''}{' '}
                          selected
                        </p>
                      </div>
                    </div>
                    <button onClick={handleClose} className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-white/60 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`} data-id="element-1642">
                      <X className="w-5 h-5" data-id="element-1643" />
                    </button>
                  </div>

                  {/* Selections List */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0" data-id="element-1644">
                    {/* Active Package Group */}
                    {activePackage && <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-cyan-500/30 bg-cyan-500/5' : 'border-teal-500/30 bg-teal-50/50'}`} data-id="element-1645">
                        {/* Package Header */}
                        <div className={`flex items-center gap-3 p-4 ${isDark ? 'bg-cyan-500/10' : 'bg-teal-50'}`} data-id="element-1646">
                          <div className={`p-2 rounded-lg ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-1647">
                            <Package className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1648" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1649">
                            <p className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-cyan-400/70' : 'text-teal-600/70'}`} data-id="element-1650">
                              Kitchen Package
                            </p>
                            <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1651">
                              {activePackage.tier} Package
                            </p>
                          </div>
                          {activePackage.price !== undefined && activePackage.price > 0 && <span className={`text-lg font-bold flex-shrink-0 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1652">
                                ${activePackage.price.toLocaleString()}
                              </span>}
                          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1653">
                            {onSwap && <button onClick={() => {
                      setIsExpanded(false);
                      onSwap?.('Kitchen', 'Kitchen Packages');
                    }} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-white/40 hover:text-cyan-400 hover:bg-cyan-500/10' : 'text-gray-400 hover:text-teal-500 hover:bg-teal-50'}`} title="Swap Package" data-id="element-1654">
                                <ArrowLeftRight className="w-4 h-4" data-id="element-1655" />
                              </button>}
                            <button onClick={clearActivePackage} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-white/40 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`} title="Remove Package" data-id="element-1656">
                              <X className="w-4 h-4" data-id="element-1657" />
                            </button>
                          </div>
                        </div>

                        {/* Package Items (nested) */}
                        <div className="p-2 space-y-1" data-id="element-1658">
                          {selections.filter(s => s.isPackageItem || isItemOverridingPackage(s.itemName)).map(selection => {
                    const isOverride = isItemOverridingPackage(selection.itemName);
                    const originalPrice = getPackageItemPrice(selection.itemName);
                    const delta = isOverride && originalPrice !== undefined ? selection.price - originalPrice : 0;
                    return <div key={selection.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${isOverride ? isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200/50' : isDark ? 'bg-white/5' : 'bg-white/80'}`} data-id="element-1659">
                                  {selection.thumbnail ? <img src={selection.thumbnail} alt={selection.optionName} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" data-id="element-1660" /> : <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`} data-id="element-1661">
                                      <Lock className={`w-3 h-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-1662" />
                                    </div>}
                                  <div className="flex-1 min-w-0" data-id="element-1663">
                                    <div className="flex items-center gap-1.5" data-id="element-1664">
                                      <p className={`text-xs font-medium truncate ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-1665">
                                        {selection.itemName}
                                      </p>
                                      {isOverride && <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`} data-id="element-1666">
                                          Upgraded
                                        </span>}
                                    </div>
                                    <p className={`text-[10px] truncate ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-1667">
                                      {selection.optionName}
                                    </p>
                                  </div>
                                  {isOverride && delta > 0 && <span className={`text-xs font-bold flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} data-id="element-1668">
                                      +${delta.toLocaleString()}
                                    </span>}
                                </div>;
                  })}
                        </div>
                      </div>}

                    {/* Non-package selections (excluding package overrides) */}
                    {selections.filter(s => !s.isPackageItem && !isItemOverridingPackage(s.itemName)).map((selection, index) => <SelectionCard key={selection.id} selection={selection} index={index} onRemove={() => removeSelection(selection.id)} onSwap={() => handleSwap(selection.category, selection.itemName)} isDark={isDark} data-id="element-1669" />)}

                    {selections.length === 0 && <div className={`text-center py-12 ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-1670">
                        <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-50" data-id="element-1671" />
                        <p data-id="element-1672">No selections yet</p>
                        <p className="text-sm mt-1" data-id="element-1673">
                          Browse options to add upgrades
                        </p>
                      </div>}
                  </div>

                  {/* Footer */}
                  {selections.length > 0 && <div className={`p-5 border-t flex-shrink-0 ${isDark ? 'border-white/10 bg-slate-900/50' : 'border-gray-200 bg-gray-50/50'}`} data-id="element-1674">
                      <div className="flex items-center justify-between mb-4" data-id="element-1675">
                        <div data-id="element-1676">
                          <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-1677">
                            Total Upgrade Value
                          </p>
                          <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1678">
                            ${totalPrice.toLocaleString()}
                          </p>
                        </div>
                        <button onClick={clearSelections} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${isDark ? 'text-red-400 hover:bg-red-500/10' : 'text-red-500 hover:bg-red-50'}`} data-id="element-1679">
                          <Trash2 className="w-4 h-4" data-id="element-1680" />
                          Clear All
                        </button>
                      </div>
                      <button onClick={handleFinalize} className={`w-full py-3.5 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 ${isDark ? 'bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400' : 'bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400'}`} data-id="element-1681">
                        <Send className="w-5 h-5" data-id="element-1682" />
                        Finalize Selections
                      </button>
                    </div>}
                </>}

              {/* Processing Step */}
              {finalizeStep === 'processing' && <div className="flex-1 flex flex-col items-center justify-center p-8" data-id="element-1683">
                  <motion.div initial={{
              scale: 0.8,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} className="text-center" data-id="element-1684">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'linear'
              }} className={`w-16 h-16 mx-auto mb-6 rounded-full border-4 border-t-transparent ${isDark ? 'border-cyan-500' : 'border-teal-500'}`} data-id="element-1685" />
                    <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1686">
                      Processing Your Selections
                    </h2>
                    <div className="space-y-3" data-id="element-1687">
                      <ProcessingStep icon={<FileText className="w-4 h-4" data-id="element-1689" />} text="Generating selection summary..." isDark={isDark} delay={0} data-id="element-1688" />
                      <ProcessingStep icon={<Building2 className="w-4 h-4" data-id="element-1691" />} text="Sending to Landmark Homes..." isDark={isDark} delay={0.8} data-id="element-1690" />
                      <ProcessingStep icon={<Mail className="w-4 h-4" data-id="element-1693" />} text="Preparing your email confirmation..." isDark={isDark} delay={1.6} data-id="element-1692" />
                    </div>
                  </motion.div>
                </div>}

              {/* Success Step */}
              {finalizeStep === 'success' && <div className="flex flex-col max-h-full overflow-hidden" data-id="element-1694">
                  {/* Scrollable content */}
                  <div className="flex-1 overflow-y-auto p-6" data-id="element-1695">
                    <motion.div initial={{
                scale: 0.8,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} className="text-center" data-id="element-1696">
                      <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  damping: 15,
                  stiffness: 200,
                  delay: 0.2
                }} className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`} data-id="element-1697">
                        <CheckCircle2 className={`w-10 h-10 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} data-id="element-1698" />
                      </motion.div>

                      <h2 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1699">
                        Selections Submitted!
                      </h2>
                      <p className={`text-sm mb-5 ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-1700">
                        Your selections have been sent to Landmark Homes
                      </p>

                      {/* Summary Card */}
                      <div className={`rounded-xl p-4 mb-4 text-left ${isDark ? 'bg-slate-800/50 border border-white/10' : 'bg-gray-50 border border-gray-200'}`} data-id="element-1701">
                        <div className="flex items-center justify-between mb-2" data-id="element-1702">
                          <span className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`} data-id="element-1703">
                            Total Upgrades
                          </span>
                          <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1704">
                            {itemCount} items
                          </span>
                        </div>
                        <div className="flex items-center justify-between mb-3" data-id="element-1705">
                          <span className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`} data-id="element-1706">
                            Total Value
                          </span>
                          <span className={`text-lg font-bold ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1707">
                            ${totalPrice.toLocaleString()}
                          </span>
                        </div>
                        <div className={`pt-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-1708">
                          <div className="flex items-center gap-2 text-sm" data-id="element-1709">
                            <Mail className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1710" />
                            <span className={isDark ? 'text-white/70' : 'text-gray-600'} data-id="element-1711">
                              Confirmation sent to{' '}
                              <span className="font-medium" data-id="element-1712">
                                demo@example.com
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* What's Next */}
                      <div className={`rounded-xl p-4 text-left ${isDark ? 'bg-cyan-500/10 border border-cyan-500/20' : 'bg-teal-50 border border-teal-200'}`} data-id="element-1713">
                        <h3 className={`font-semibold mb-3 ${isDark ? 'text-cyan-400' : 'text-teal-700'}`} data-id="element-1714">
                          What happens next?
                        </h3>
                        <ul className={`text-sm space-y-3 ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-1715">
                          <li className="flex gap-3" data-id="element-1716">
                            <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-1717" />
                            <span data-id="element-1718">
                              A design consultant will review your selections
                            </span>
                          </li>
                          <li className="flex gap-3" data-id="element-1719">
                            <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-1720" />
                            <span data-id="element-1721">
                              Receive a detailed quote within 24-48 hours
                            </span>
                          </li>
                          <li className="flex gap-3" data-id="element-1722">
                            <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-1723" />
                            <span data-id="element-1724">
                              Schedule a call to finalize your dream home
                            </span>
                          </li>
                        </ul>
                      </div>
                    </motion.div>
                  </div>

                  {/* Fixed Actions at bottom */}
                  <div className={`p-5 border-t flex-shrink-0 space-y-2 ${isDark ? 'border-white/10 bg-slate-900/80' : 'border-gray-200 bg-white/80'}`} data-id="element-1725">
                    <button onClick={handleClose} className={`w-full py-3.5 rounded-xl font-bold text-white transition-all ${isDark ? 'bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400' : 'bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400'}`} data-id="element-1726">
                      Continue Exploring
                    </button>
                    <button onClick={handleStartOver} className={`w-full py-2.5 rounded-xl font-medium transition-all ${isDark ? 'text-white/60 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`} data-id="element-1727">
                      Start New Selection
                    </button>
                  </div>
                </div>}
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}
interface ProcessingStepProps {
  icon: React.ReactNode;
  text: string;
  isDark: boolean;
  delay: number;
}
function ProcessingStep({
  icon,
  text,
  isDark,
  delay
}: ProcessingStepProps) {
  return <motion.div initial={{
    opacity: 0,
    x: -10
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    delay
  }} className={`flex items-center gap-3 ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-1728">
      <div className={`p-1.5 rounded-lg ${isDark ? 'bg-white/10' : 'bg-gray-100'}`} data-id="element-1729">
        {icon}
      </div>
      <span className="text-sm" data-id="element-1730">{text}</span>
    </motion.div>;
}
interface SelectionCardProps {
  selection: Selection;
  index: number;
  onRemove: () => void;
  onSwap?: () => void;
  isDark: boolean;
}
function SelectionCard({
  selection,
  index,
  onRemove,
  onSwap,
  isDark
}: SelectionCardProps) {
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} transition={{
    delay: index * 0.05
  }} className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${isDark ? 'bg-slate-800/50 border-white/5 hover:border-white/10' : 'bg-white border-gray-100 hover:border-gray-200 shadow-sm'}`} data-id="element-1731">
      {/* Thumbnail */}
      {selection.thumbnail ? <img src={selection.thumbnail} alt={selection.optionName} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" data-id="element-1732" /> : <div className={`w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`} data-id="element-1733">
          <ShoppingBag className={`w-5 h-5 ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-1734" />
        </div>}

      {/* Info */}
      <div className="flex-1 min-w-0" data-id="element-1735">
        <p className={`text-xs font-medium uppercase tracking-wider mb-0.5 ${isDark ? 'text-cyan-400/70' : 'text-teal-600/70'}`} data-id="element-1736">
          {selection.category}
        </p>
        <p className={`font-semibold truncate text-sm ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1737">
          {selection.itemName}
        </p>
        <p className={`text-xs truncate ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-1738">
          {selection.optionName}
        </p>
      </div>

      {/* Price & Actions */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0" data-id="element-1739">
        <span className={`text-base font-bold ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1740">
          ${selection.price.toLocaleString()}
        </span>
        <div className="flex items-center gap-1" data-id="element-1741">
          {onSwap && <button onClick={onSwap} className={`p-1 rounded-lg transition-colors ${isDark ? 'text-white/40 hover:text-cyan-400 hover:bg-cyan-500/10' : 'text-gray-400 hover:text-teal-500 hover:bg-teal-50'}`} title="Swap Item" data-id="element-1742">
              <ArrowLeftRight className="w-4 h-4" data-id="element-1743" />
            </button>}
          <button onClick={onRemove} className={`p-1 rounded-lg transition-colors ${isDark ? 'text-white/40 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`} title="Remove Item" data-id="element-1744">
            <X className="w-4 h-4" data-id="element-1745" />
          </button>
        </div>
      </div>
    </motion.div>;
}